import isArray from 'lodash/isArray';
import memoize from 'lodash/memoize';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import pick from 'lodash/pick';
import isPlainObject from 'lodash/isPlainObject';
import { template } from '@zedoc/text';
import {
  estimateTheta,
  getInformation,
  getParamsFromConfig,
  getParamsByValueOrder,
  getStandardError,
  applyClinicalScoreMap,
  hasNegativeAlphaItems,
  detectCalibrationClass,
  extractRSMParams,
} from '@zedoc/cat-irt';
import { QUESTION_TYPE__CAT_SECTION } from '../constants';
import Formula from '../models/Formula';
import { formatVariableValue } from './helpers';
import {
  INSUFFICIENT_DATA_TEXT,
  isInsufficientDataError,
} from './insufficientData';

const getDefaultMutations = (evaluationScope) => {
  const questionnaire = evaluationScope.getQuestionnaire();
  return questionnaire && questionnaire.groupMutationsByQuestionId();
};

const dynamicProperties = ['votesToHide', 'votesToHideChunk'];

const templateProperties = ['title', 'description'];

// Stability cache for the randomization features so Math.random() values
// don't flicker across re-renders. Keyed by sectionId; invalidated when
// questionnaire id, response count for the section, or initialTheta change.
//
// review:accepted: module-level state is safe here because every call site
// is client-side (zedoc-patient-web, zedoc-form-builder, react-questionnaire)
// where each browser tab gets its own module instance — no cross-patient
// leakage vector. Cache-thrash when a single tab navigates between
// questionnaires is a minor re-computation cost, not a correctness bug.
// If this ever needs to run server-side (e.g. SSR of the patient app or
// server-side scoring), the cache must be replaced with a per-call/per-
// request store — do NOT keep the module-level Map in that scenario.
const randomizationCache = new Map();
let lastQuestionnaireId = null;

/**
 * Create a tree structure (similar to form values), that represents current
 * dynamic properties values for all questions.
 *
 * @param {EvaluationScope} evaluationScope
 * @param {Object} [options]
 * @param {Object} [options.mutationsByQuestionId]
 * @param {Object} [options.propertiesDefaults]
 * @param {Object} [options.propertiesOverrides]
 */
function evaluateDynamicProperties(
  evaluationScope,
  {
    propertiesOverrides,
    mutationsByQuestionId = getDefaultMutations(evaluationScope),
  } = {},
) {
  const questionnaire = evaluationScope.getQuestionnaire();
  if (!questionnaire) {
    throw new Error(
      'evaluateDynamicProperties requires scope bound to a questionnaire',
    );
  }
  // Cross-render stability for the randomization features. Wipe cached
  // random values when the questionnaire changes.
  const currentQuestionnaireId = questionnaire._id;
  if (currentQuestionnaireId !== lastQuestionnaireId) {
    randomizationCache.clear();
    lastQuestionnaireId = currentQuestionnaireId;
  }
  const evaluate = memoize((formula) => formula.evaluate(evaluationScope));
  const getVariable = (id, { exclamationMark, modifier }) => {
    let result;
    const question = questionnaire.getQuestionById(id);
    if (question) {
      result = evaluationScope.lookupAnswer(id);
    } else {
      result = evaluationScope.lookupVariable(id, exclamationMark);

      if (result) {
        const schema = questionnaire.getVariableSchema(id);

        result.value = formatVariableValue({
          schema,
          value: result.value,
          language: questionnaire.language,
        });
      }
    }
    if (!result) {
      return null;
    }
    if (isInsufficientDataError(result.error)) {
      return INSUFFICIENT_DATA_TEXT;
    }
    if (result.error) {
      return '#ERR';
    }
    if (question) {
      switch (modifier) {
        case 'label': {
          if (question) {
            const label = question.getAnswerLabel(result);
            if (isArray(label)) {
              return label.join(', ');
            }
            if (typeof label === 'string') {
              return label;
            }
            return null;
          }
          break;
        }
        default: {
          // ...
        }
      }
    }
    return result.value;
  };

  const getIRTContext = memoize((sectionId) => {
    const section = questionnaire.getQuestionById(sectionId);
    if (!section || section.type !== QUESTION_TYPE__CAT_SECTION) {
      return undefined;
    }
    const formula = section.getIrtConfigFormula();
    if (formula) {
      const result = evaluate(Formula.create(formula));
      if (!result.error && isPlainObject(result.value)) {
        const config = result.value;
        const responses = questionnaire.createResponsesFromFormValues(
          evaluationScope.toFormValues(),
          {
            sectionId: section.id,
          },
        );
        // Calibration class detection. Singapore confirmed SHAW-CAT is Andrich
        // RSM with discrimination fixed at 1.0 (the "alpha" field is δ_i,
        // "beta" is the section-shared τ_j vector). PROMIS-style banks remain
        // 2PL GRM. The dispatcher in @zedoc/cat-irt routes by params shape, so
        // we build the right shape here.
        //
        // For RSM: live loop uses MLE end-to-end, matching the paper's
        // Kwan et al. (2024) simulation methodology. The patient-facing
        // T-score continues to use MLE via FormulaIRTCalculation.
        //
        // For GRM (PROMIS): live loop defaults to BM (Bayes Modal — catR's
        // convention) which regularises θ via the standard-normal prior so
        // SE-based stopping fires reliably across response patterns.
        const calibrationClass = detectCalibrationClass(config);
        const isShawCatStyle = hasNegativeAlphaItems(config);

        let params;
        let effectiveResponses;
        let scoringMethod;
        if (calibrationClass === 'RSM') {
          // RSM path — Andrich rating-scale. Direction handling is in the
          // calibration's `score` field (which the engine reads directly);
          // no α-sign flip needed.
          //
          // review:manual: extractRSMParams throws on malformed items
          // (missing/non-numeric alpha, <2 coefficients, empty τ vector,
          // non-numeric score). detectCalibrationClass can route here on a
          // partially-authored config — τ-lengths match across items but
          // one item's α/score is transiently blank. Live per-render CAT
          // scoring will hard-crash instead of degrading; prod calibrations
          // are safe (all-or-nothing loaded from checked-in JSON) but the
          // form-builder preview is the plausible trigger. Wrap in try/catch
          // + GRM fallback (or NoData) before shipping RSM authoring to
          // form-builder end-users. Kept as-is now because Singapore
          // SHAW-CAT calibrations are only edited by developers.
          params = extractRSMParams(config);
          effectiveResponses = responses;
          scoringMethod = section.getScoringMethod() || 'ML';
        } else {
          // GRM path — legacy 2PL. Preserve the existing direction handling.
          const parameterOrdering =
            section.getParameterOrdering() ||
            (isShawCatStyle ? 'BY_VALUE' : 'BY_BETA');
          const responseScoreMap =
            section.getResponseScoreMap() ||
            (isShawCatStyle ? 'CLINICAL' : 'NATIVE');
          params =
            parameterOrdering === 'BY_VALUE'
              ? getParamsByValueOrder(config)
              : getParamsFromConfig(config);
          effectiveResponses =
            responseScoreMap === 'CLINICAL'
              ? applyClinicalScoreMap(responses, config)
              : responses;
          scoringMethod = section.getScoringMethod() || 'BM';
        }

        // Feature 1: randomize first item (scope variable `randomize`)
        const randomizeVar = evaluationScope.lookupVariable('randomize', true);
        const shouldRandomize = !!(randomizeVar && randomizeVar.value === true);

        // Feature 3: randomesque top-N selection (`randomize_next`, `top_n`)
        const randomizeNextVar = evaluationScope.lookupVariable(
          'randomize_next',
          true,
        );
        const shouldRandomizeNext = !!(
          randomizeNextVar && randomizeNextVar.value === true
        );
        const topNVar = evaluationScope.lookupVariable('top_n', true);
        const topN =
          topNVar && typeof topNVar.value === 'number' && topNVar.value >= 2
            ? topNVar.value
            : 2;

        // Feature 2: use previous theta from a forInternalUsage question's
        // variable as the section's initialTheta.
        const sectionQuestions = questionnaire.getChildQuestions(section.id);
        const internalQuestion = sectionQuestions.find(
          (q) => q.forInternalUsage && q.variableId,
        );
        let initialTheta = null;
        if (internalQuestion) {
          const v = evaluationScope.lookupVariable(
            internalQuestion.variableId,
            true,
          );
          if (v && v.value !== undefined && v.value !== null) {
            initialTheta = Number(v.value);
          }
        }
        if (initialTheta === null && !shouldRandomize) {
          initialTheta = 0;
        }

        const theta = estimateTheta(params, effectiveResponses, {
          method: scoringMethod,
          initialTheta,
        });
        const standardError = getStandardError(
          params,
          effectiveResponses,
          theta,
          { method: scoringMethod },
        );
        const catMaxItems = section.getCATMaxItems();
        const minItems = section.getMinItems();
        let done = false;
        // catMaxItems is the hard upper bound and always fires when reached.
        if (catMaxItems !== undefined && responses.length >= catMaxItems) {
          done = true;
        }
        // SE-based stopping is gated by minItems (clinical-safety floor):
        // even if SE has dropped below threshold, do not terminate before
        // minItems responses have been collected. Prevents premature
        // 2-question PF terminations under fast convergence on RSM+MLE.
        const standardErrorThreshold = section.getStandardErrorThreshold();
        if (
          standardErrorThreshold !== undefined &&
          standardError <= standardErrorThreshold &&
          responses.length >= minItems
        ) {
          done = true;
        }

        // Pre-compute the random selections once per cache window so the UI
        // doesn't reorder candidates across re-renders before the patient
        // actually answers.
        const cacheKey = section.id;
        const cached = randomizationCache.get(cacheKey);
        const cacheStillValid =
          cached &&
          cached.responsesCount === responses.length &&
          cached.initialTheta === initialTheta &&
          cached.shouldRandomize === shouldRandomize &&
          cached.shouldRandomizeNext === shouldRandomizeNext &&
          cached.topN === topN;

        let randomValues = null;
        let randomesqueWinner = null;
        let randomesqueMaxInfo = 0;
        const shouldRandomizeFirst = responses.length === 0 && shouldRandomize;

        if (cacheStillValid) {
          randomValues = cached.randomValues;
          randomesqueWinner = cached.randomesqueWinner;
          randomesqueMaxInfo = cached.randomesqueMaxInfo;
        } else {
          if (shouldRandomizeFirst) {
            randomValues = {};
            sectionQuestions.forEach((q) => {
              if (!q.forInternalUsage) {
                randomValues[q.id] = Math.random();
              }
            });
          }
          if (
            shouldRandomizeNext &&
            responses.length > 0 &&
            !shouldRandomizeFirst
          ) {
            const answeredIds = new Set(responses.map((r) => r.questionId));
            const available = sectionQuestions
              .filter(
                (q) =>
                  !q.forInternalUsage && params[q.id] && !answeredIds.has(q.id),
              )
              .map((q) => ({
                id: q.id,
                information: getInformation(params, theta, q.id),
              }))
              .sort((a, b) => b.information - a.information);
            if (available.length >= topN) {
              const top = available.slice(0, topN);
              const idx = Math.floor(Math.random() * topN);
              randomesqueWinner = top[idx].id;
              randomesqueMaxInfo = top[0].information;
            } else if (available.length >= 2) {
              const idx = Math.floor(Math.random() * available.length);
              randomesqueWinner = available[idx].id;
              randomesqueMaxInfo = available[0].information;
            }
          }
          randomizationCache.set(cacheKey, {
            responsesCount: responses.length,
            initialTheta,
            shouldRandomize,
            shouldRandomizeNext,
            topN,
            randomValues,
            randomesqueWinner,
            randomesqueMaxInfo,
          });
        }

        return {
          params,
          done,
          responses,
          theta,
          standardError,
          shouldRandomizeFirst,
          randomValues,
          shouldRandomizeNext,
          randomesqueWinner,
          randomesqueMaxInfo,
        };
      }
    }
    return undefined;
  });

  const nextResult = {};
  questionnaire.forEachQuestion(
    (question) => {
      const {
        _elements: elementsPropertiesOverrides,
        ...ownPropertiesOverride
      } = propertiesOverrides
        ? {
            ...propertiesOverrides['*'],
            ...propertiesOverrides[question.id],
          }
        : {};

      const mutations = mutationsByQuestionId[question.id];
      const initialValue = pick(question, dynamicProperties);

      let nextValue = reduce(
        mutations,
        (current, { formula, transform }) => {
          const { value, error } = evaluate(formula);
          if (!error && value) {
            return transform(current);
          }
          return current;
        },
        initialValue,
      );

      forEach(templateProperties, (prop) => {
        if (question[prop]) {
          // NOTE: In the future we may consider more sophisticated templating
          //       functions, e.g. conditionals or loops. If this is the case
          //       then templates will need to be compiled at the question level
          //       and cached.
          const value = template(question[prop])(getVariable);
          if (value !== question[prop]) {
            nextValue = {
              ...nextValue,
              [prop]: value,
            };
          }
        }
      });

      const section = questionnaire.getQuestionById(question.sectionId);
      if (section && section.type === QUESTION_TYPE__CAT_SECTION) {
        const context = getIRTContext(section.id);
        const element = evaluationScope.getAnswer(question.id);
        if (element && element.sequenceNo !== undefined) {
          nextValue = {
            ...nextValue,
            sequenceNo: element.sequenceNo,
          };
        } else if (context && context.done) {
          nextValue = {
            ...nextValue,
            votesToHide: (nextValue.votesToHide || 0) + 1,
          };
        } else if (context) {
          let information;
          if (
            context.shouldRandomizeFirst &&
            context.randomValues &&
            context.randomValues[question.id] !== undefined
          ) {
            // Feature 1: first item is picked uniformly at random from the
            // section's non-internal questions. Replacing `information` with
            // a stable random number per question lets the existing
            // "highest information wins" scheduler do the picking.
            information = context.randomValues[question.id];
          } else {
            information = getInformation(
              context.params,
              context.theta,
              question.id,
            );
            if (
              context.shouldRandomizeNext &&
              context.randomesqueWinner === question.id
            ) {
              // Feature 3: boost the randomesque-selected item's reported
              // information so it edges out the top-1 by Fisher info.
              information = context.randomesqueMaxInfo + 0.0001;
            }
          }
          nextValue = {
            ...nextValue,
            information,
          };
        }
      }

      if (question.isCollection()) {
        const answer = evaluationScope.getAnswer(question.id);
        const newElements = {};
        forEach(answer && answer._elementsOrder, (elementId) => {
          const elementOverrides =
            elementsPropertiesOverrides &&
            elementsPropertiesOverrides[elementId] &&
            elementsPropertiesOverrides[elementId]._elements;
          newElements[elementId] = {
            _elements: evaluateDynamicProperties(
              evaluationScope
                .getOrCreateSubScope(question.id)
                .getOrCreateSubScope(elementId),
              {
                mutationsByQuestionId,
                propertiesOverrides: {
                  ...elementOverrides,
                  '*': {
                    ...(propertiesOverrides && propertiesOverrides['*']),
                    ...(elementOverrides && elementOverrides['*']),
                  },
                },
              },
            ),
          };
          // NOTE: This means nothing interesting happened there, and the
          //       nested properties are equal to the initial values.
          if (!newElements[elementId]._elements) {
            delete newElements[elementId];
          }
        });
        if (!isEmpty(newElements)) {
          nextValue = {
            ...nextValue,
            _elements: newElements,
          };
        }
      }

      if (!isEmpty(ownPropertiesOverride)) {
        nextValue = {
          ...nextValue,
          ...ownPropertiesOverride,
        };
      }

      const diffObject = {};
      forEach(nextValue, (value, key) => {
        if (value !== question[key]) {
          diffObject[key] = value;
        }
      });

      if (!isEmpty(diffObject)) {
        nextResult[question.id] = diffObject;
      }
    },
    {
      sectionId: evaluationScope.getCollectionQuestionId(),
      stopRecursion: (q) => q.isCollection(),
    },
  );
  if (isEmpty(nextResult)) {
    return null;
  }
  return nextResult;
}

export default evaluateDynamicProperties;
